import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';

import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import axios from 'axios';
import Loader from '../general/Loader';
import SaveButton from '../general/SaveButton';
import { useUnsavedChanges } from '../../utils/custom-hooks';

import nlLocale from '@fullcalendar/core/locales/nl';
import daLocale from '@fullcalendar/core/locales/da';
import enLocale from '@fullcalendar/core/locales/en-gb';

import { Button } from '../ui-kit';
import { IconUndo, IconArrowLeft, IconArrowRight } from '../ui-kit/icons';

const ONE_DAY = 60 * 60 * 24; // seconds
const MAX_DAYS = 14 * 7; // 14 weeks
const LARGE_SCREEN_THRESHOLD = 21; // days

const locale = (() => {
  if (I18n.locale.startsWith('da')) {
    return daLocale;
  } else if (I18n.locale.startsWith('nl')) {
    return nlLocale;
  }

  return enLocale;
})();

// Testing instructions:
//
// - make changes to the schedule and click save. Then reload to verify that the
//   changes were persisted.
// - add a day
// - remove a day
// - move the anchor date forward/backward
// - make changes and then click reset to verify that the changes are reverted
// - create an interval and move it before the start date to check clipping
// - create an interval and move it after the start date to check clipping
// - check that the exception schedule background matches the recurring schedule
// - resize interval
// - delete interval

import {
  clipIntervalsToScheduleLength,
  intervalId,
  intervalsToEvents,
  removeOverlap,
  useCurrentDay,
  utcMoment
} from './utils';
import { splitIntervalsByDay } from '../../utils/schedules';

import './RecurringSchedule.scss';

const plugins = [timeGridPlugin, interactionPlugin];
const slotLabelFormat = { hour: '2-digit', minute: '2-digit', hour12: false };

// RecurringSchedule renders and (optionally) allows editing of a recurring
// schedule: a pattern of availability that repeats every `scheduleLength`
// weeks, starting from `anchorDate`.
const RecurringSchedule = ({ ownerType, ownerId, editable }) => {
  const [anchorDate, setAnchorDate] = useState(null);
  const [scheduleLength, setScheduleLength] = useState(0);
  const [intervals, setIntervals] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const calendarRef = useRef(null);
  // snapshot of the last saved state, used to revert unsaved changes
  const [savedState, setSavedState] = useState({
    scheduleLength: null,
    intervals: []
  });

  // warn the user before navigating away (closing the tab, reloading, etc.)
  // if there are unsaved changes
  useUnsavedChanges(hasUnsavedChanges);

  const currentDay = useCurrentDay(anchorDate, scheduleLength);

  const updateScheduleFromBackend = useCallback(data => {
    const { intervals, anchor_date, schedule_length } = data;

    const anchorTime = utcMoment(anchor_date);
    const cleanedIntervals = intervals.map(interval => ({
      start_time: moment(anchorTime).add(interval.start_time_offset, 'seconds').format(),
      end_time: moment(anchorTime).add(interval.end_time_offset, 'seconds').format(),
      available: interval.available
    }));

    const availableIntervals = splitIntervalsByDay(
      cleanedIntervals.filter(interval => interval.available),
      'UTC'
    );
    setIntervals(availableIntervals);
    setAnchorDate(anchor_date);
    setScheduleLength(schedule_length);
    setSavedState({ intervals: availableIntervals, scheduleLength: schedule_length });
  }, []);

  useEffect(() => {
    axios.get(`/api/v2/${ownerType}s/${ownerId}/recurring_schedule`).then(response => {
      updateScheduleFromBackend(response.data);
    });
  }, [ownerType, ownerId, updateScheduleFromBackend]);

  useEffect(() => {
    // if the schedule length exceeds a certain threshold, add container-fluid
    // class to the #main-container. Otherwise, remove it. This is to ensure that
    // the calendar fits within the viewport.
    const scheduleLengthInDays = scheduleLength / ONE_DAY;
    const mainContainer = document.querySelector('#main-container');
    if (!mainContainer) return;

    if (scheduleLengthInDays > LARGE_SCREEN_THRESHOLD) {
      mainContainer.classList.add('container-fluid');
      mainContainer.classList.remove('container');
    } else {
      mainContainer.classList.add('container');
      mainContainer.classList.remove('container-fluid');
    }

    // toggling the container class changes the width available to the
    // calendar, but FullCalendar doesn't observe that change on its own, so
    // it keeps rendering at the old size until some other interaction (e.g.
    // a window resize) forces it to recalculate. Explicitly nudge it to
    // update its size once the browser has applied the new layout.
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      requestAnimationFrame(() => calendarApi.updateSize());
    }
  });

  const anchorTime = useMemo(() => (anchorDate ? utcMoment(anchorDate) : null), [anchorDate]);

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi && anchorTime) calendarApi.gotoDate(anchorTime.format());
  }, [anchorTime]);

  const events = useMemo(() => intervalsToEvents(intervals), [intervals]);

  const addInterval = useCallback((start, end, available) => {
    setIntervals(prev => [...removeOverlap(prev, start, end), { start_time: start, end_time: end, available }]);
    setHasUnsavedChanges(true);
  }, []);

  const handleSelect = useCallback(
    selectionInfo => {
      if (!editable || isSaving) return;
      const { startStr, endStr } = selectionInfo;

      // full calendar returns the start and end times in the local timezone,
      // but we want to store them in the backend timezone, so we append the
      // timezone offset string to the start and end times before adding the
      // rnterval.
      const startTime = utcMoment(startStr);
      const minEndTime = moment(startTime).add(1, 'hour');
      const endTime = utcMoment(endStr);

      // a plain click (as opposed to a drag) produces a selection that spans
      // only a single slot (15 minutes here). In that case, default the new
      // interval to be at least 1 hour long instead of just the slot size.
      const clampedEndTime = endTime.isBefore(minEndTime) ? minEndTime : endTime;

      addInterval(startTime.format(), clampedEndTime.format(), true);
      calendarRef.current?.getApi().unselect();
    },
    [addInterval, editable, isSaving]
  );

  const createFullDayInterval = useCallback(
    date => {
      if (!editable || isSaving) return;
      const start = utcMoment(date).startOf('day').format();
      const end = utcMoment(start).add(1, 'day').format();
      addInterval(start, end, true);
    },
    [addInterval, editable, isSaving]
  );

  // clicking an existing interval removes it (i.e. marks it unavailable again)
  const handleEventClick = useCallback(
    clickInfo => {
      if (!editable || isSaving) return;
      const id = clickInfo.event.id;
      setIntervals(prev => prev.filter(interval => intervalId(interval) !== id));
      setHasUnsavedChanges(true);
    },
    [editable, isSaving]
  );

  // dragging or resizing an interval: replace the old interval with the new one
  const handleEventChange = useCallback(changeInfo => {
    const { event, oldEvent } = changeInfo;
    const oldId = oldEvent.id;

    setIntervals(prev => {
      const withoutOld = prev.filter(interval => intervalId(interval) !== oldId);
      const withoutOverlap = removeOverlap(withoutOld, event.startStr, event.endStr);
      return [
        ...withoutOverlap,
        { start_time: event.startStr, end_time: event.endStr, available: event.extendedProps.available }
      ];
    });
    setHasUnsavedChanges(true);
  }, []);

  // dayCellClassNames/dayHeaderClassNames are re-evaluated on every render
  // (unlike dayCellDidMount/dayHeaderDidMount, which only fire once when the
  // cell's DOM element is first mounted), so they correctly reflect
  // `currentDay` whenever it changes.
  const dayCellClassNames = useCallback(
    arg => (currentDay && utcMoment(arg.date).isSame(currentDay, 'day') ? ['recurring-schedule-current-day'] : []),
    [currentDay]
  );

  const dayHeaderClassNames = useCallback(
    arg => (currentDay && utcMoment(arg.date).isSame(currentDay, 'day') ? ['recurring-schedule-current-day'] : []),
    [currentDay]
  );

  // custom day header: rendered as a plain DOM node (rather than through
  // React) so the whole cell area, not just the day number, is clickable to
  // fill the entire day.
  const dayHeaderContent = useCallback(
    arg => {
      const dayNumber = utcMoment(arg.date).diff(utcMoment(anchorDate), 'days') + 1;
      const scheduleLengthInDays = scheduleLength / ONE_DAY;
      const isMultipleOfWeek = scheduleLengthInDays % 7 === 0;
      const dayName = utcMoment(arg.date).format(scheduleLength > LARGE_SCREEN_THRESHOLD ? 'dd' : 'ddd');
      const content = isMultipleOfWeek ? dayName : dayNumber;

      const el = document.createElement('div');
      el.className = 'recurring-schedule-day-header';
      if (scheduleLengthInDays > LARGE_SCREEN_THRESHOLD) {
        el.style.fontSize = '80%';
      }
      el.appendChild(document.createTextNode(content));

      // when displaying more than one full week, show the week number below
      // the day name so it's clear which week each day belongs to.
      if (isMultipleOfWeek && scheduleLengthInDays > 7) {
        const weekNumber = Math.floor((dayNumber - 1) / 7) + 1;
        const content = `W${weekNumber}`;
        el.appendChild(document.createElement('br'));
        el.appendChild(document.createTextNode(content));
      }

      if (editable) {
        el.classList.add('recurring-schedule-day-header-clickable');
        el.addEventListener('click', () => createFullDayInterval(arg.date));
      }

      return { domNodes: [el] };
    },
    [anchorDate, editable, createFullDayInterval, scheduleLength]
  );

  const resetChanges = () => {
    setScheduleLength(savedState.scheduleLength);
    setIntervals(savedState.intervals);
    setHasUnsavedChanges(false);
  };

  const addDays = daysToAdd => {
    const newScheduleLength = Math.min(scheduleLength + daysToAdd * ONE_DAY, MAX_DAYS * ONE_DAY);
    setScheduleLength(newScheduleLength);
    setHasUnsavedChanges(true);
  };

  const removeDays = daysToRemove => {
    const newScheduleLength = Math.max(scheduleLength - daysToRemove * ONE_DAY, ONE_DAY);
    setScheduleLength(newScheduleLength);
    setIntervals(prev => clipIntervalsToScheduleLength(prev, utcMoment(anchorDate), newScheduleLength));
    setHasUnsavedChanges(true);
  };

  const addDay = () => addDays(1);
  const addWeek = () => addDays(7);
  const removeDay = () => removeDays(1);
  const removeWeek = () => removeDays(7);

  const moveAnchorDate = deltaDays => {
    const newAnchorDate = moment(anchorDate).add(deltaDays, 'days').format();

    const backendCall = () => {
      setIsSaving(true);
      axios
        .put(`/api/v2/${ownerType}s/${ownerId}/recurring_schedule`, { anchor_date: newAnchorDate })
        .then(response => {
          updateScheduleFromBackend(response.data);
        })
        .catch(error => {
          console.error('Error moving anchor date:', error);
        })
        .finally(() => {
          setIsSaving(false);
        });
    };

    if (hasUnsavedChanges) {
      save().then(backendCall);
    } else {
      backendCall();
    }
  };

  const save = () => {
    const nextState = { scheduleLength, intervals };
    setSavedState(nextState);
    setHasUnsavedChanges(false);
    setIsSaving(true);
    const anchorTime = utcMoment(anchorDate);

    const clippedIntervals = clipIntervalsToScheduleLength(intervals, anchorTime, scheduleLength);

    const params = {
      anchor_date: anchorDate,
      schedule_length: scheduleLength,
      intervals: clippedIntervals.map(interval => ({
        start_time_offset: moment(interval.start_time).diff(anchorTime, 'seconds'),
        end_time_offset: moment(interval.end_time).diff(anchorTime, 'seconds'),
        available: true
      }))
    };

    return axios
      .put(`/api/v2/${ownerType}s/${ownerId}/recurring_schedule`, params)
      .then(response => {
        updateScheduleFromBackend(response.data);
      })
      .catch(error => {
        console.error('Error saving recurring schedule:', error);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  if (!anchorDate) {
    return <Loader />;
  }

  const scheduleLengthInDays = scheduleLength / ONE_DAY;

  return (
    <div className={`recurring-schedule${scheduleLengthInDays > 14 ? ' recurring-schedule-compact' : ''}`}>
      <div className="recurring-schedule-toolbar d-flex justify-content-between align-items-center mb-2">
        <div>
          {editable && (
            <Button
              variant="outline-secondary"
              onClick={resetChanges}
              title={I18n.t('reset')}
              disabled={!hasUnsavedChanges || isSaving}
            >
              <IconUndo />
            </Button>
          )}
        </div>

        {editable && (
          <div className="d-flex align-items-center recurring-schedule-toolbar">
            <Button
              variant="outline-secondary"
              className="me-2"
              onClick={moveAnchorDate.bind(null, 1)}
              disabled={isSaving}
            >
              <IconArrowLeft />
            </Button>
            {I18n.t('schedules.recurring_schedule.move_anchor_date')}
            <Button
              className="ms-2 me-5"
              variant="outline-secondary"
              onClick={moveAnchorDate.bind(null, -1)}
              disabled={isSaving}
            >
              <IconArrowRight />
            </Button>

            <Button
              variant="outline-secondary"
              className="me-2"
              onClick={removeWeek}
              disabled={scheduleLength <= 7 * ONE_DAY || isSaving}
            >
              - {I18n.t('week.one', { count: 1 })}
            </Button>
            <Button
              variant="outline-secondary"
              className="me-2"
              onClick={removeDay}
              disabled={scheduleLength <= ONE_DAY || isSaving}
            >
              - {I18n.t('day.one', { count: 1 })}
            </Button>
            <Button
              variant="outline-secondary"
              className="me-2"
              onClick={addDay}
              disabled={scheduleLength >= MAX_DAYS * ONE_DAY || isSaving}
            >
              + {I18n.t('day.one', { count: 1 })}
            </Button>
            <Button
              variant="outline-secondary"
              onClick={addWeek}
              disabled={scheduleLength >= MAX_DAYS * ONE_DAY || isSaving}
            >
              + {I18n.t('week.one', { count: 1 })}
            </Button>
          </div>
        )}

        <div>{editable && <SaveButton onClick={save} hasUnsavedChanges={hasUnsavedChanges} isSaving={isSaving} />}</div>
      </div>

      <FullCalendar
        ref={calendarRef}
        locale={locale}
        plugins={plugins}
        timeZone="UTC" // ensure it works the same in all browsers
        dayCount={scheduleLength / ONE_DAY}
        headerToolbar={false}
        dayHeaderContent={dayHeaderContent}
        dayHeaderClassNames={dayHeaderClassNames}
        initialView="timeGrid"
        allDaySlot={false}
        slotDuration="00:15:00"
        slotLabelFormat={slotLabelFormat}
        slotLabelInterval={{ hours: 1 }}
        slotEventOverlap={false}
        height="auto"
        editable={editable && !isSaving}
        eventStartEditable={editable && !isSaving}
        eventResizableFromStart={editable && !isSaving}
        selectable={editable && !isSaving}
        select={handleSelect}
        eventClick={handleEventClick}
        eventDrop={handleEventChange}
        eventResize={handleEventChange}
        events={events}
        themeSystem="bootstrap5"
        dayCellClassNames={dayCellClassNames}
      />
    </div>
  );
};

export default RecurringSchedule;
